import { SCENE_INTERACTION } from "../../constants/scene";
import { getChessMessages, getGlobalMessages, getLocalRoomMessages, getPrivateMessages, SOCKET_EVENTS } from "../../socket/Socket";
import { formatMessageTime, privateMessageMap, roomMessageMap } from "../engine/helpers/chatHelper";
import { getSceneId } from "../MVC/dataHelpers";
import { SCENE_EVENTS } from "../MVC/Model";
import { types } from "./ChatPanelTitle";
import { ThreeDotsSingleChatMenu } from "./UI/SingleChat-ThreeDots";
import { singleChatPanel, messageSent, messageReceived, messageReceivedPvt } from "./UI/SingleChatComponent-UI";
import { isMobile } from "../engine/helpers/worldHelpers";
import { EventDispatcher } from "three";
import { CHAT_EVENTS } from "../../constants/chat";
import { ReactiveComponent } from "../../core/ReactiveComponent.core";
import { AMPLITUDE_EVENTS, chatTypeForAnalytics, trackAnalytics } from "../Tracking/baglessAnalytics";
import { watchConversation, setConversationView } from "./conversationView";

class ChatLoadMore extends ReactiveComponent {
  constructor({ socket, otherUserId, onHistoryLoaded, initialPagination = null }) {
    const container = $('<div class="chat-load-more" style="padding: 8px; text-align: center;"></div>');
    super(container, { status: 'loaded', pagination: initialPagination });
    this.socket = socket;
    this.otherUserId = otherUserId;
    this.onHistoryLoaded = onHistoryLoaded;
    this.lastPage = (initialPagination && typeof initialPagination.currentPage !== 'undefined')
      ? Number(initialPagination.currentPage)
      : null;
    this.render();
    this.bindEvents();
    this.updateVisibility();
  }
  render() {
    this.$el.html(`
      <button class="button is-small" data-role="load-more">Load more</button>
    `);
    this.$button = this.$el.find('[data-role="load-more"]');
    this.applyStatus();
  }
  bindEvents() {
    this.$el.on('click', '[data-role="load-more"]', () => this.loadMore());
  }
  setPagination(pagination) {
    this.state.pagination = pagination || null;
    if (pagination && typeof pagination.currentPage !== 'undefined') {
      this.lastPage = Number(pagination.currentPage);
    }
    this.updateVisibility();
  }
  hasMore() {
    const p = this.state.pagination;
    if (!p) return false;
    if (typeof p.has_more !== 'undefined') return !!p.has_more;
    if (typeof p.pageCount !== 'undefined' && typeof p.currentPage !== 'undefined') {
      return Number(p.currentPage) + 1 < Number(p.pageCount);
    }
    if (typeof p.total_pages !== 'undefined' && typeof p.page !== 'undefined') {
      return Number(p.page) + 1 < Number(p.total_pages);
    }
    if (typeof p.next_page !== 'undefined') return p.next_page !== null && p.next_page !== false;
    return false;
  }
  getNextPage() {
    const p = this.state.pagination || {};
    if (typeof p.currentPage !== 'undefined') return Number(p.currentPage) + 1; // 0-based
    if (typeof p.page !== 'undefined') return Number(p.page) + 1;
    if (typeof p.next_page !== 'undefined' && p.next_page !== null) return Number(p.next_page);
    if (this.lastPage !== null) return Number(this.lastPage) + 1;
    return 1;
  }
  getPerPage() {
    const p = this.state.pagination || {};
    return Number(p.perPage || p.per_page || p['per-page'] || 50);
  }
  setStatus(status) {
    this.state.status = status;
    this.applyStatus();
  }
  applyStatus() {
    if (!this.$button) return;
    const isLoading = this.state.status === 'loading';
    this.$button.prop('disabled', isLoading);
    this.$button.text(isLoading ? 'Loading…' : 'Load more');
  }
  updateVisibility() {
    this.$el.toggle(this.hasMore());
  }
  loadMore() {
    if (this.state.status === 'loading') return;
    this.setStatus('loading');
    const page = this.getNextPage() + 1;
    const perPage = this.getPerPage();
    this.socket._requestChatHistory(this.otherUserId, (response) => {
      this.setStatus('loaded');
      this.setPagination(response?.pagination || null);
      this.onHistoryLoaded && this.onHistoryLoaded(response);
    }, page, perPage);
  }
}

/**
 * @class NewChatComponent
 * Represents a chat component for handling user or room chats.
* Creates an instance of NewChatComponent.
* @param {Object} params - Configuration parameters for the chat component.
* @param {string} params.id - Unique identifier for the chat instance.
* @param {string} params.name - Display name of the chat (e.g., Room or User name).
* @param {string} params.type - Type of the chat (e.g., "ROOM",  or "PRIVATE").
* @param {Function} params.historyApi - API function to fetch chat history.
* @param {string} params.userId - User ID associated with the chat.
*/
class NewChatComponent extends EventDispatcher {

  constructor(data) {
    super();

    this.container = $(singleChatPanel(data));
    this.threeDotsMenu = $(ThreeDotsSingleChatMenu());
    this.threeMenuOpened = false;

    // Selectors
    this.backButton = this.container.find(".back-button");
    this.closeButton = this.container.find(".header-actions .icon-close");
    this.threeButtons = this.container.find('.action-button--icon-three-dots');
    this.optionsButton = this.container.find(".header-actions .action-button[aria-label='Chat options']");
    this.messagesContainer = this.container.find(".messages-container");
    this.inputField = this.container.find("#messageInput");
    this.emojiButton = this.container.find(".icon-emoji");
    this.emojiMenu = this.container.find(".message-input__emoji-menu");
    this.emojiIcons = this.container.find(".message-input__emoji-menu__item");
    this.sendButton = this.container.find(".input-actions .icon-send");

    // Selectors for each menu item
    this.inviteToScene = this.threeDotsMenu.find("#chatPvt-inviteToScene");
    this.jumpToScene = this.threeDotsMenu.find("#chatPvt-jumpToScene");
    this.removeFromFriends = this.threeDotsMenu.find("#chatPvt-removeFromFriends");
    this.showUserInfo = this.threeDotsMenu.find("#chatPvt-showInfo");
    this.startOneOnOne = this.threeDotsMenu.find("#chatPvt-startOneOnOne");
    this.updatedUserNames = {};
    if (data?.isGuest) {
      this.removeFromFriends.hide();
    }
    if (data.type !== types.PRIVATE) {
      this.startOneOnOne.hide();
    }
    this.container.find(".header-actions").append(this.threeDotsMenu);
    this.threeDotsMenu.hide();
    if (isMobile()) {
      this.emojiButton.hide()
    }

    this.data = data;
    this.model = data.model;
    if (this.model && this.model.socket && this.data.type === types.PRIVATE) {
      const conversation = watchConversation(() => this.model.socket.getSocket());
      this.model.socket.addEventListener(SOCKET_EVENTS.SOCKET_READY, () => conversation.report());
    }
    this._chatMessageN = 0;
    this.historyMessages = [];
    this.user = {
      id: data.id,
      userName: data.name,
      userAvatarUrl: data.userAvatarUrl,
    }

    if (this.data.type === types.PRIVATE) {
      const user = this.model.socket.getUsersObject()[this.data.id];
      if (user) {
        this.user = user;
      }
    }

    // Initialize
    this.initialize();
    if (!(this.model.config.isGuest && this.data.type === types.PRIVATE)) {
      this.loadHistoryMessages(data.type, data.id);
    }
  }

  initialize() {
    this.bindEvents();
    this.container.hide();
    $("body").append(this.container);
    if (this.data.type === types.PRIVATE) {
      this.chatLoadMore = new ChatLoadMore({
        socket: this.model.socket,
        otherUserId: this.data.id,
        onHistoryLoaded: (response) => this.onMoreHistoryLoaded(response),
        initialPagination: null,
      });
      // Place inside messages container at the top
      this.messagesContainer.prepend(this.chatLoadMore.$el);
    }
    this.close();
  }

  bindEvents() {
    this.backButton.on("click", () => this.onBack());
    this.closeButton.on("click", () => {
      this.close();
      this.model.dispatchEvent({ type: CHAT_EVENTS.CLOSE_BUTTON_CLICKED_ON_SINGLE_CHAT });
    });
    this.sendButton.on("click", () => this.sendMessage());
    this.inputField.on('focus', (event) => {
      setTimeout(() => {
        event.target.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300); // Delay allows the keyboard to fully open
    });
    this.inputField.on("keypress", (e) => {
      this.emojiMenu.hide();

      if (e.key === "Enter") {
        e.preventDefault();
        this.sendMessage();
      }
    });

    this.emojiButton.on('click', () => {
      this.emojiMenu.toggle();
    })
    $(document).on('click', (e) => {
      if (this.emojiButton.is(e.target) || this.emojiButton.has(e.target).length !== 0) {
        return
      }
      if (!this.emojiMenu.is(e.target) && this.emojiMenu.has(e.target).length === 0) {
        this.emojiMenu.hide();
      }
    })
    this.emojiIcons.on('click', (e) => {
      this.inputField.val(this.inputField.val() + e.currentTarget.innerText);
      this.inputField.trigger('focus');
    })

    // Event Listeners
    this.inviteToScene.on("click", () => {
      const userId = this.data.id;
      const user = this.model.socket.getAllOnlineUsersObject()[userId];
      this.model.inviteUserToRoom(user);
      this.closeThreeDostsMenu();
    });

    this.jumpToScene.on("click", () => {
      this.closeThreeDostsMenu();
      const userId = this.data.id;
      const user = this.model.socket.getAllOnlineUsersObject()[userId];
      if (user.worldId !== this.model.socket.getUserData().worldId) {
        const callback = () => {
          window.location = `${window.location.protocol}//${window.location.host}/worlds/view?id=${user.worldId}&security=${this.model.config.security}&loc=${user.currentSceneId}`;
        }
        this.model.hideSceneElements();
        this.model.dispatchEvent({ type: SCENE_EVENTS.START_ANIMTAION_OUTRO, data: { onEndCallback: () => { callback() } } })
      } else {
        const sceneId = getSceneId(user, this.model.sceneData.scenes);
        this.model.dispatchEvent({
          type: SCENE_INTERACTION.JUMP_TO_SCENE, data: {
            sceneId: sceneId
          }
        });
      }
    });

    this.removeFromFriends.on("click", () => {
      this.model.worldFetcher.removeFriend(this.data.id, (response) => {
        this.model.info('success', `Removed from friends list`);
        this.model.onFriendAddedSuccess(response);
        this.removeFromFriends.hide();
      });
      this.closeThreeDostsMenu();
    });

    this.showUserInfo.on("click", () => {
      if (typeof this.data.id !== "number") {
        this.model.showGuestModal(this.data.id);
      } else {
        this.model.loadAndPopulateUserInfo(this.data.id, {})
        $("#guestAndOtherUserProfileModal").addClass("is-active");
      }
      this.closeThreeDostsMenu();
    });

    this.startOneOnOne.on("click", () => {
      const userId = this.data.id;
      const user = this.model.socket.getUsersObject()?.[userId]
        || this.model.socket.getAllOnlineUsersObject()?.[userId];
      this.model.startOneOnOneTourWith(user || { userId, id: userId });
      this.closeThreeDostsMenu();
    });
    this.threeButtons.on("click", (e) => {
      e.stopPropagation();

      console.log("View Profile clicked");
      if (this.threeMenuOpened) {
        this.closeThreeDostsMenu();
      } else {
        this.openThreeDotsMenu();
      }
    });

    if (this.data.type === types.ROOM) {
      this.model.socket.addEventListener(SOCKET_EVENTS.MESSAGE_RECEIVED, () => { this.setMessages() });
      this.model.socket.addEventListener(SOCKET_EVENTS.GLOBAL_MESSAGE_SENT, () => { this.setMessages() });
      this.model.addEventListener(SCENE_INTERACTION.SOCKET_USER_INFO_CHANGED, (e) => {
        this.updateUserNames(e.data.userId);
      });
    }
    if (this.data.type === types.SCENE) {
      this.model.socket.addEventListener(SOCKET_EVENTS.LOCAL_MESSAGE_RECEIVED, () => { this.setMessages() });
      this.model.addEventListener(SCENE_INTERACTION.SOCKET_USER_INFO_CHANGED, (e) => {
        this.updateUserNames(e.data.userId);
      });
    }
    if (this.data.type === types.PRIVATE) {
      this.model.socket.addEventListener(SOCKET_EVENTS.ONE_ONE_ONE_MESSAGE_RECEIVED, () => { this.setMessages() });
      this.model.socket.addEventListener(SOCKET_EVENTS.USER_ONE_ON_ONE_SENT, () => { this.setMessages() });
      this.model.addEventListener(SCENE_INTERACTION.SOCKET_USER_INFO_CHANGED, (e) => {
        this.updateUserNames(e.data.userId);
        this.updateTitle(e.data.userId);
      });
      this.model.addEventListener(SCENE_EVENTS.SOMEONE_JOINED_ROOM, () => {
        this.updateThreeDotsMenuItems();
      });
      this.model.socket.addEventListener(SOCKET_EVENTS.USER_DISCONNECTED, () => {
        this.updateThreeDotsMenuItems();
      });
      this.updateThreeDotsMenuItems();

      this.model.addEventListener(SCENE_EVENTS.ON_FRIENDS_SET, (e) => {
        this.updateRemoveFromFriends();
      });
    }
    if (this.data.type === types.CHESS) {
      this.model.socket.addEventListener(SOCKET_EVENTS.CHESS_MESSAGE_RECEIVED, () => { this.setMessages() });
      this.model.addEventListener(SCENE_INTERACTION.SOCKET_USER_INFO_CHANGED, (e) => {
        this.updateUserNames(e.data.userId);
      });
    }
    const closeEvents = [
      SCENE_EVENTS.NAVBAR_ITEM_CLICKED,
      SCENE_INTERACTION.PEOPLE_PANEL_OPENED,
      SCENE_INTERACTION.CHAT_PANEL_OPENED,
    ];
    closeEvents.forEach((event) => {
      this.model.addEventListener(event, (e) => {
        this.close();
      });
    });
  }

  open() {
    const wasOpen = this.container.is(':visible');
    this.container.show();
    this.setMessages();
    this.inputField.val("");
    const chatTitle = this.container.find(".room-title");
    if (this.data.type === types.PRIVATE) {
      chatTitle.text(this.data.name);
      setConversationView(this.data.id, true);
    } else if (this.data.type === types.ROOM) {
      chatTitle.text(`${$trans('room-chat-header-text')}:${this.model.sceneData.name}`);
    } else if (this.data.type === types.SCENE) {
      const user = this.model.socket.getUsersObject()[this.model.config.userId];
      chatTitle.text(`${$trans('current-room')}:${user.currentSceneName}`);
    } else if (this.data.type === types.CHESS) {
    }
    this.dispatchEvent({ type: 'opened' });
    if (this.data.type === types.PRIVATE) {
      if (!this?.data?.isGuest) {
        this.updateRemoveFromFriends();
      }
    }
    if (!wasOpen) {
      trackAnalytics(this.model, AMPLITUDE_EVENTS.OPENED_CONVERSATION, {
        chat_type: chatTypeForAnalytics(this.data.type),
        t: Date.now(),
      });
    }
  }

  close() {
    const wasOpen = this.container.is(':visible');
    if (wasOpen && this.data.type === types.PRIVATE) {
      setConversationView(this.data.id, false);
    }
    this.container.hide();
    this.closeThreeDostsMenu();
    this.model.resetChatState();

    this.dispatchEvent({ type: 'closed' });
    if (wasOpen) {
      trackAnalytics(this.model, AMPLITUDE_EVENTS.CLOSED_CONVERSATION, {
        chat_type: chatTypeForAnalytics(this.data.type),
        t: Date.now(),
      });
    }
  }
  closeThreeDostsMenu() {
    this.threeMenuOpened = false;
    this.threeDotsMenu.hide();
  }
  openThreeDotsMenu() {
    this.threeMenuOpened = true;
    this.threeDotsMenu.show();
  }

  onBack() {
    console.log("Back button clicked");
    $('.world-bottom-nav__area__chat').trigger("click")
    this.close();
  }

  sendMessage() {
    const userId = this.data.id;
    const message = this.inputField.val().trim();
    if (!message) return;
    const properID = userId || id;
    if (!message || message === "" || message === " ") {
      return
    }
    const isPerson = this.data.type === types.PRIVATE;
    if (isPerson && !this.model.isUserOnline(properID)) {
      this.model.sendOfflineMessagesModal.open();
      const user = this.model.friendsMap[properID];
      this.model.sendOfflineMessagesModal.template.find(".local-avatar").attr("src", user.avatar_image_url);
      ///add callback on user is offline modal
      this.model.sendOfflineMessagesModal.onOk = () => {
        this.onSendOfflineMessage(message);
        this.inputField.val("");
        this.close();
      }
      return;
    }
    this.inputField.val("");
    this.inputField.trigger("focus");
    this.inputField.val("");
    this._trackSentChatMessage();
    if (this.data.type === types.ROOM)
      this.onSendRoomMessage(message)
    if (this.data.type === types.SCENE)
      this.onSendSceneMessage(message)
    if (this.data.type === types.PRIVATE)
      this.onSendPrivateMessage(message)
    if (this.data.type === types.TOUR)
      this.onSendTourMessage(message)
    if (this.data.type === types.CHESS)
      this.onSendChessMessage(message)
  }

  _trackSentChatMessage(extra = {}) {
    this._chatMessageN += 1;
    trackAnalytics(this.model, AMPLITUDE_EVENTS.SENT_CHAT_MESSAGE, {
      chat_type: chatTypeForAnalytics(this.data.type),
      message_n: this._chatMessageN,
      t: Date.now(),
      ...extra,
    });
  }

  addMessage({ text, senderName = "Unknown", timestamp = "Now", action = "sent", senderId = null, callId = '' }) {
    if (action === 'call') {
      this.appendCallEvent(text, timestamp, callId);
      return;
    }
    let messageHTML;
    const userName = this.updatedUserNames[senderId] || senderName;

    // Convert URLs in text to clickable links
    const textWithLinks = this.convertUrlsToLinks(text);

    if (this.data.type === types.PRIVATE) {
      messageHTML = action === "sent" ? messageSent({ text: textWithLinks, timestamp })
        : messageReceivedPvt({ text: textWithLinks, senderName: userName, timestamp });
    } else {
      messageHTML = action === "sent" ? messageSent({ text: textWithLinks, timestamp })
        : messageReceived({ text: textWithLinks, timestamp, senderName: userName, senderId });
    }
    this.messagesContainer.append(messageHTML);
    this.messagesContainer.scrollTop(this.messagesContainer[0].scrollHeight);
  }

  appendCallEvent(text, timestamp, callId) {
    const note = document.createElement('div');
    note.className = 'spaces-call-outcome';
    if (callId) {
      note.dataset.callId = String(callId);
    }
    const label = document.createElement('p');
    label.textContent = text || 'You missed a call.';
    const time = document.createElement('time');
    time.textContent = timestamp || '';
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Call back';
    button.addEventListener('click', () => {
      if (this.model && typeof this.model.startOneOnOneVideoCall === 'function') {
        this.model.startOneOnOneVideoCall(this.data.id);
      }
    });
    note.append(label, time, button);
    this.messagesContainer.append(note);
    const target = callId ? note : null;
    if (target && this.focusCallId && String(this.focusCallId) === String(callId)) {
      note.scrollIntoView({ block: 'nearest' });
    }
  }

  prependHistoryMessages(messages) {
    if (!messages || !messages.length) return;
    const containerEl = this.messagesContainer[0];
    const prevScrollHeight = containerEl.scrollHeight;
    const prevScrollTop = containerEl.scrollTop;
    const html = messages.map(({ type, payload }) => {
      const textWithLinks = this.convertUrlsToLinks(payload.text);
      if (this.data.type === types.PRIVATE) {
        return (type === "sent")
          ? messageSent({ text: textWithLinks, timestamp: payload.timestamp })
          : messageReceivedPvt({ text: textWithLinks, timestamp: payload.timestamp });
      } else {
        return (type === "sent")
          ? messageSent({ text: textWithLinks, timestamp: payload.timestamp })
          : messageReceived({ text: textWithLinks, timestamp: payload.timestamp, senderName: payload.senderName, senderId: payload.senderId });
      }
    }).join('');
    if (this.chatLoadMore && this.chatLoadMore.$el && this.chatLoadMore.$el.length) {
      this.chatLoadMore.$el.after(html);
    } else {
      this.messagesContainer.prepend(html);
    }
    const newScrollHeight = containerEl.scrollHeight;
    containerEl.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
  }


  /**
   * Converts URLs in text to clickable links
   * @param {string} text - The text to process
   * @returns {string} - Text with URLs converted to HTML links
   */
  convertUrlsToLinks(text) {
    if (!text || typeof text !== 'string') {
      return text;
    }


    // More robust URL regex pattern
    // Matches: http://, https://, www., and common TLDs
    const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+\.[a-z]{2,}|[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}(:[0-9]{1,5})?(\/.*)?)/gi;

    return text.replace(urlRegex, (url) => {
      // Skip if it's already an HTML link
      if (url.includes('<a href=') || url.includes('</a>')) {
        return url;
      }

      // Ensure URL has protocol
      let fullUrl = url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        fullUrl = 'https://' + url;
      }

      // Clean up the URL (remove trailing punctuation)
      fullUrl = fullUrl.replace(/[.,;!?]+$/, '');

      // Validate the URL
      try {
        new URL(fullUrl);
        return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="chat-link">${url}</a>`;
      } catch (e) {
        // If URL is invalid, return the original text
        return url;
      }
    });
  }


  setMessages() {
    if (this.chatLoadMore) {
      // Preserve load more button; clear only message items
      this.messagesContainer.find('.message-sent, .message-received, .spaces-call-outcome').remove();
    } else {
      this.messagesContainer.empty();
    }
    let latestMessages = [];
    let chatTitle;
    if (this.data.type === types.PRIVATE) {
      latestMessages = getPrivateMessages()[this.data.id] || [];
      chatTitle = $(`#chatUI-${this.data.id}`);
    }
    if (this.data.type === types.ROOM) {
      latestMessages = getGlobalMessages() || [];
      chatTitle = $(`#chatUI-single-chat-world`);
    }
    if (this.data.type === types.SCENE) {
      latestMessages = getLocalRoomMessages()[this.data.id] || [];
      chatTitle = $(`#chatUI-${this.data.id}`);
    }
    if (this.data.type === types.CHESS) {
      latestMessages = getChessMessages()[this.data.id] || [];
      chatTitle = $(`#chatUI-${this.data.id}`);
    }
    const allMessages = [...this.historyMessages, ...latestMessages];
    this.addMessages(allMessages);
    if (chatTitle && allMessages.length > 0) {
      const { payload } = allMessages[allMessages.length - 1];
      chatTitle.find(".chat-message").html(`${payload.senderName}: ${payload.text}`);
      chatTitle.find(".chat-time").html(`${payload.timestamp ?? formatMessageTime(payload.time)}`);
    } else if (chatTitle) {
      chatTitle.find(".chat-message").html(``);
      chatTitle.find(".chat-time").html(``);
    }
  }

  loadHistoryMessages(type, id) {
    let onHistoryLoadedCallback;
    const currentUserId = this.model.config.userId;

    switch (type) {
      case types.ROOM:
        onHistoryLoadedCallback = (response) => {
          this.historyMessages = roomMessageMap(response, currentUserId);
          this.setMessages(this.historyMessages);
          const chatTitle = $(`#chatUI-single-chat-world`);
          this.setChatTitle(chatTitle, this.historyMessages);
        };
        this.model.getGlobalHistoryChatMessages(this.model.config.worldId, onHistoryLoadedCallback);
        break;

      case types.PRIVATE:
        onHistoryLoadedCallback = (response) => {
          // response structure from getPrivateMessages:
          // { status: 'success', data: [ { sender_id, receiver_id, message, timestamp, ... } ], pagination: {...} }
          const messages = (response && response.data) ? response.data : [];
          const normalized = messages.slice().reverse().map(m => ({
            userId: m.sender_id,
            senderId: m.sender_id,
            message: m.message,
            timestamp: m.timestamp,
            kind: m.kind || '',
            callId: m.call_id || '',
          }));
          const lastMessage = normalized[normalized.length - 1];
          this.historyMessages = privateMessageMap(normalized, currentUserId, this.user.userName);
          this.setMessages(this.historyMessages);
          // set initial pagination to load-more, if available
          this.chatLoadMore && this.chatLoadMore.setPagination(response?.pagination || null);
          const chatTitle = $(`#chatUI-${this.data.id}`);
          if (chatTitle && this.historyMessages.length > 0) {
            const { payload } = this.historyMessages[this.historyMessages.length - 1];
            chatTitle.find(".chat-message").html(`${payload.senderName}: ${payload.text}`);
            lastMessage && lastMessage.timestamp && chatTitle.attr("last-timestamp", new Date(lastMessage.timestamp).getTime());
          }
        };
        this.model.socket._requestChatHistory(id, onHistoryLoadedCallback);
        break;

      case types.SCENE:
        onHistoryLoadedCallback = (response) => {
          this.historyMessages = roomMessageMap(response, currentUserId);
          this.setMessages(this.historyMessages);
          const chatTitle = $(`#chatUI-${this.data.id}`);
          this.setChatTitle(chatTitle, this.historyMessages);
        };
        this.model.getRoomHistoryChatMessages(this.data.id, onHistoryLoadedCallback);
        break;

      default:
        console.warn("Unknown message type:", type);
    }
  }

  ///SEND LOGIc
  onSendRoomMessage = (message) => {  /// aka onSendWorld
    this.model.socket.addUserMessage(message);
    this.model.socket.sendMessage(message);
    this.setMessages();
  }

  onSendSceneMessage = (message) => {
    const id = this.data ? this.data.id : this.model.currentScene.id;
    this.model.socket.sendMessage(message, id, "local");
    this.setMessages();
  }
  onSendPrivateMessage = (message) => {
    const { data } = this;
    this.model.socket.onSendMessagePvt(
      {
        message: message,
        userName: data.name,
        userId: data.id,
        receiverType: "user",
        toSave: data.id
      }
    );
    this.setMessages();
  }

  onSendTourMessage = (message) => {
    // const { data } = this.state;
    // this.model.socket.addUserMessage(message, true, data);
    this.model.socket.sendMessage(message, null, "tour");
    // addTourMessage({ ...this.state.data, text: message }, "sent");
    // socket.dispatchEvent({ type: SOCKET_EVENTS.TOUR_MESSAGE_SENT });
  }

  onSendChessMessage = (message) => {
    this.model.socket.sendMessage(message, this.data.id, "chess");
    this.setMessages();
  }

  onSendOfflineMessage = (message) => {
    this.model.socket.onSendOfflinePvt(
      {
        message: message,
        userName: this.user.userName,
        userId: this.user.id,
      }
    );
    this.model.socket.dispatchEvent({ type: SOCKET_EVENTS.OFFLINE_MESSAGE_SENT, data: { ...this.user, text: message } });
    this.model.socket.addUserMessage(message, true, { userId: this.data.id });
    this.setMessages();
    this._trackSentChatMessage({ offline: true });
  }

  addMessages(messages) {
    messages.forEach((message) => {
      const { type, payload } = message;
      this.addMessage({
        text: payload.text,
        senderId: payload.senderId,
        senderName: payload.senderName,
        timestamp: payload.timestamp,
        action: type,
        callId: payload.callId,
      });
    });
  }

  setChatTitle(chatTitle, messages) {
    if (chatTitle && messages.length > 0) {
      const { payload } = messages[messages.length - 1];
      chatTitle.find(".chat-message").html(`${payload.senderName}: ${payload.text}`);
      chatTitle.find(".chat-time").html(`${payload.timestamp ?? formatMessageTime(payload.time)}`);
    } else {
      if (chatTitle) {
        chatTitle.find(".chat-message").html(``);
        chatTitle.find(".chat-time").html(``);
      }
    }
  }
  updateUserNames(senderId) {
    const messages = this.messagesContainer.find(`[data-participant-id="${senderId}"]`);
    const user = this.model.socket.getUsersObject()[senderId];
    this.updatedUserNames[senderId] = user.userName;
    messages.each((index, element) => {
      $(element).text(user.userName);
    });
  }
  updateTitle(senderId) {
    const privateUserName = this.updatedUserNames[senderId];
    const chatTitle = this.container.find(".room-title");
    chatTitle && privateUserName && chatTitle.text(privateUserName);
  }
  updateThreeDotsMenuItems() {
    const user = this.model.socket.getUsersObject()[this.data.id];
    if (user) {
      this.inviteToScene.removeClass("disabled");
      this.jumpToScene.removeClass("disabled");
      if (typeof this.data.id !== "number") {
        this.showUserInfo.removeClass("disabled");
      }
    } else {
      this.inviteToScene.addClass("disabled");
      this.jumpToScene.addClass("disabled");
      if (typeof this.data.id !== "number") {
        this.showUserInfo.addClass("disabled");
      }
    }
  }
  updateRemoveFromFriends() {
    const { online, offline } = this.model.friends;
    const isOffline = offline.some((item) => item.id === this.data.id);
    if (isOffline) {
      this.removeFromFriends.hide();
    }
    const isOnline = online.some((item) => item.id === this.data.id);
    if (isOnline) {
      this.removeFromFriends.show();
    }
    this.updateThreeDotsMenuItems();
  }
  onMoreHistoryLoaded(response) {
    if (!response) return;
    const currentUserId = this.model.config.userId;
    const messages = (response && response.data) ? response.data : [];
    const normalized = messages.slice().reverse().map(m => ({
      userId: m.sender_id,
      senderId: m.sender_id,
      message: m.message,
      timestamp: m.timestamp
    }));
    // normalized.reverse();
    const older = privateMessageMap(normalized, currentUserId, this.user.userName);
    this.historyMessages = [...older, ...this.historyMessages];
    this.prependHistoryMessages(older);
  }
}

export default NewChatComponent;
